import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Icons } from '../../shared/ui';
const { ChevronLeft: ChevronLeftIcon, ChevronRight: ChevronRightIcon, Calendar: CalendarDaysIcon, ChevronDown: ChevronDownIcon } = Icons;

const CustomCalendarToolbar = ({ label, date, view, onNavigate, onView }) => {
  const { t } = useTranslation('calendar');
  const MONTHS = t('toolbar.monthFull', { returnObjects: true });
  const VIEWS = [
    { key: 'month', label: t('toolbar.view.month') },
    { key: 'week', label: t('toolbar.view.week') },
    { key: 'day', label: t('toolbar.view.day') },
    { key: 'agenda', label: t('toolbar.view.agenda') },
  ];
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const monthPickerRef = useRef(null);
  const yearPickerRef = useRef(null);

  const currentMonth = moment(date).month();
  const currentYear = moment(date).year();

  // Generate year range (10 years before and after current year)
  const currentRealYear = moment().year();
  const years = [];
  for (let y = currentRealYear - 10; y <= currentRealYear + 10; y++) {
    years.push(y);
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(event.target)) {
        setShowMonthPicker(false);
      }
      if (yearPickerRef.current && !yearPickerRef.current.contains(event.target)) {
        setShowYearPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const goToToday = () => {
    onNavigate('TODAY');
  };

  const goToPrev = () => {
    onNavigate('PREV');
  };

  const goToNext = () => {
    onNavigate('NEXT');
  };

  const handleMonthSelect = (monthIndex) => {
    const newDate = moment(date).month(monthIndex).toDate();
    onNavigate('DATE', newDate);
    setShowMonthPicker(false);
  };

  const handleYearSelect = (year) => {
    const newDate = moment(date).year(year).toDate();
    onNavigate('DATE', newDate);
    setShowYearPicker(false);
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-stone-50 border-b border-stone-200">
      {/* Left: Navigation & Date Selection */}
      <div className="flex items-center gap-2">
        {/* Today Button */}
        <button
          onClick={goToToday}
          className="px-4 py-2 bg-white border border-stone-200 rounded-lg text-sm font-medium text-stone-700 hover:bg-stone-50 hover:border-stone-300 transition-all shadow-sm"
        >
          {t('toolbar.today')}
        </button>

        {/* Navigation Arrows */}
        <div className="flex items-center bg-white border border-stone-200 rounded-lg shadow-sm overflow-hidden">
          <button
            onClick={goToPrev}
            className="p-2 hover:bg-stone-50 transition-colors border-r border-stone-200"
            title={t('toolbar.prev')}
          >
            <ChevronLeftIcon size={20} className="text-stone-600" />
          </button>
          <button
            onClick={goToNext}
            className="p-2 hover:bg-stone-50 transition-colors"
            title={t('toolbar.next')}
          >
            <ChevronRightIcon size={20} className="text-stone-600" />
          </button>
        </div>

        {/* Month Picker */}
        <div className="relative" ref={monthPickerRef}>
          <button
            onClick={() => {
              setShowMonthPicker(!showMonthPicker);
              setShowYearPicker(false);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-lg text-sm font-semibold text-stone-700 hover:bg-stone-50 hover:border-stone-300 transition-all shadow-sm min-w-[120px] justify-between"
          >
            <span>{MONTHS[currentMonth]}</span>
            <ChevronDownIcon size={16} className={`text-stone-500 transition-transform ${showMonthPicker ? 'rotate-180' : ''}`} />
          </button>

          {showMonthPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in-up">
              <div className="grid grid-cols-3 gap-1 p-2 w-[280px]">
                {MONTHS.map((month, index) => (
                  <button
                    key={month}
                    onClick={() => handleMonthSelect(index)}
                    className={`px-3 py-2 text-sm rounded-lg transition-all ${
                      index === currentMonth
                        ? 'bg-teal-600 text-white font-semibold'
                        : 'text-stone-700 hover:bg-teal-50 hover:text-teal-700'
                    }`}
                  >
                    {month}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Year Picker */}
        <div className="relative" ref={yearPickerRef}>
          <button
            onClick={() => {
              setShowYearPicker(!showYearPicker);
              setShowMonthPicker(false);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-lg text-sm font-semibold text-stone-700 hover:bg-stone-50 hover:border-stone-300 transition-all shadow-sm min-w-[100px] justify-between"
          >
            <span>{currentYear}</span>
            <ChevronDownIcon size={16} className={`text-stone-500 transition-transform ${showYearPicker ? 'rotate-180' : ''}`} />
          </button>

          {showYearPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in-up">
              <div className="max-h-[280px] overflow-y-auto p-2 w-[200px] custom-scrollbar">
                <div className="grid grid-cols-3 gap-1">
                  {years.map((year) => (
                    <button
                      key={year}
                      onClick={() => handleYearSelect(year)}
                      className={`px-3 py-2 text-sm rounded-lg transition-all ${
                        year === currentYear
                          ? 'bg-teal-600 text-white font-semibold'
                          : year === currentRealYear
                          ? 'bg-teal-100 text-teal-700 font-medium'
                          : 'text-stone-700 hover:bg-teal-50 hover:text-teal-700'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Center: Current Date Display */}
      <div className="flex items-center gap-2 text-lg font-bold text-stone-800">
        <CalendarDaysIcon size={20} className="text-teal-600" />
        <span>{label}</span>
      </div>

      {/* Right: View Selector */}
      <div className="flex items-center bg-white border border-stone-200 rounded-lg shadow-sm overflow-hidden">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            onClick={() => onView(v.key)}
            className={`px-4 py-2 text-sm font-medium transition-all ${
              view === v.key
                ? 'bg-teal-600 text-white'
                : 'text-stone-600 hover:bg-stone-50'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CustomCalendarToolbar;
